import { subscriptionCost } from "constants/subscriptions";

import React, { useEffect } from "react";

import { KeyboardArrowDown } from "@mui/icons-material";
import { Box, Button, Grid, Stack, useMediaQuery, useTheme } from "@mui/material";
import Typography from "@mui/material/Typography";
import Panel from "components/elements/Panel";
import { Check, Credit } from "components/icons";
import ModalDowngradingFail from "components/modules/Subscription/ModalDowngradingFail";
import ModalNotEnoughCredits from "components/modules/Subscription/ModalNotEnoughCredits";
import { useSubscription } from "components/modules/Subscription/module/useSubscription";
import SubscriptionBadge from "components/modules/SubscriptionBadge";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { SubscriptionType } from "types/enums";

import { Badge, Card, SubscriptionButton } from "./styles";

const features = {
  [SubscriptionType.FREE]: ["item1", "item2", "item3", "item4", "item5", "item6", "item7"],
  [SubscriptionType.PREMIUM]: ["item1", "item2", "item3", "item4", "item5", "item6", "item7", "item8"],
  [SubscriptionType.PRO]: ["item1", "item2", "item3", "item4", "item5", "item6", "item7", "item8", "item9", "item10"],
};

const Subscription: React.FC = () => {
  const { t } = useTranslation();
  const { colors } = useMUIThemeModeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    actualSubscription,
    isLoading,
    setIsLoading,
    modalNotEnoughCreditsOpen,
    modalDowngradingFailOpen,
    subscriptionResponse,
    onModalDowngradingFailClose,
    onModalNotEnoughCredits,
    changeSubscription,
    creditsRechargeType,
  } = useSubscription();

  useEffect(() => {
    if (isLoading) setIsLoading(false);
  }, [actualSubscription]);

  const getSubscriptionCost = (type: SubscriptionType) => {
    return (
      <Stack spacing={1} direction="row" alignItems="center">
        <Stack spacing={0.5} direction="row" alignItems="center">
          <Typography fontWeight={fontWeight.bold} fontSize={"1.5rem"} color={colors.white}>
            {subscriptionCost[type]}
          </Typography>
          <Credit sx={{ fontSize: 24 }} />
        </Stack>
        <Typography fontWeight={fontWeight.regular} fontSize={"0.875rem"}>
          {`/ ${t("datetime.month")} `}
        </Typography>
      </Stack>
    );
  };

  const getSubscriptionButton = (subscriptionType: SubscriptionType) => {
    const isActive = subscriptionType === actualSubscription;

    return (
      <SubscriptionButton
        size="large"
        fullWidth
        subscription={subscriptionType}
        disabled={isLoading || isActive}
        onClick={() => changeSubscription(subscriptionType)}>
        {isActive ? <><Check sx={{ color: colors.success, fontSize: 20, marginRight: 0.75 }} /> {t("activeAlready")}</> : t("activate")}
      </SubscriptionButton>
    );
  };

  const getFeature = (subscriptionType: SubscriptionType, feature: string, checkColor: string) => {
    return (
      <Stack key={`${subscriptionType}-${feature}`} direction="row" spacing={1} alignItems="flex-start">
        <Check sx={{ color: checkColor, fontSize: 20, marginTop: 0.5 }} />
        <Typography color={"inherit"} fontWeight={fontWeight.light}>
          <Trans i18nKey={`subscription.features.${subscriptionType}.${feature}`} />
        </Typography>
      </Stack>
    );
  };

  const getAutomaticMonthlyPaymentButton = () => {
    return (
      <>
        <Box flexGrow={1} />
        <Button
          variant="text"
          fullWidth
          component={Link}
          to="#mesicni-platba"
          endIcon={<KeyboardArrowDown />}
          sx={{
            "&.MuiButton-root": {
              color: colors.white,
            },
            "& .MuiButton-endIcon": {
              color: theme.palette.primary.main,
            },
            "&:hover": {
              backgroundColor: "transparent",
            },
          }}>
          {t("subscription.automaticMonthlyPayment")}
        </Button>
      </>
    );
  };

  return (
    <Panel sx={{ mb: 4, p: { xs: 2, md: 3 } }} id="predplatne">
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ pb: 1, color: "tertiary.main" }}>
        {t("subscription.title")}
      </Typography>
      <Typography variant="subtitle2" fontWeight={400} sx={{ pb: 3 }}>
        <Trans
          i18nKey="subscription.description"
          components={{
            aSubscriptions: <Link style={{ color: theme.palette.primary.main }} to="/predplatne" />,
            aBadges: <Link style={{ color: theme.palette.primary.main }} to="#odznaky" />,
            aBitcoinerChallenge: <Link style={{ color: theme.palette.primary.main }} to="/vyzva-bitcoinera" />,
            aAff: <Link style={{ color: theme.palette.primary.main }} to="#affiliate" />,
            aRecharge: <Link style={{ color: theme.palette.primary.main }} to="#mesicni-platba" />,
            aPackage: <Link style={{ color: theme.palette.primary.main }} to="#balicky" />,
          }}
        />
      </Typography>
      <Grid container spacing={{ xs: 2, md: 3 }} alignItems={"stretch"}>
        <Grid item xs={12} lg={4} height="auto">
          <Card subscription={SubscriptionType.FREE}>
            <SubscriptionBadge disable subscription={SubscriptionType.FREE} />
            <Typography fontWeight={fontWeight.bold} color={colors.black} fontSize={"1.5rem"}>
              {t("foreverFree")}
            </Typography>
            {getSubscriptionButton(SubscriptionType.FREE)}
            <Stack direction={"column"} spacing={1.5}>
              {features[SubscriptionType.FREE].map((feature) => getFeature(SubscriptionType.FREE, feature, colors.black))}
            </Stack>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4} height="auto">
          <Card subscription={SubscriptionType.PREMIUM}>
            <Badge bgcolor={theme.palette.primary.main}>
              <Typography>{t("subscription.badge.PREMIUM")}</Typography>
            </Badge>
            <SubscriptionBadge disable subscription={SubscriptionType.PREMIUM} />
            {getSubscriptionCost(SubscriptionType.PREMIUM)}
            {getSubscriptionButton(SubscriptionType.PREMIUM)}
            <Stack direction={"column"} spacing={1.5}>
              {features[SubscriptionType.PREMIUM].map((feature) => getFeature(SubscriptionType.PREMIUM, feature, colors.white))}
            </Stack>
            {actualSubscription === SubscriptionType.PREMIUM
              && !creditsRechargeType
              && getAutomaticMonthlyPaymentButton()}
          </Card>
        </Grid>
        <Grid item xs={12} lg={4} height="auto">
          <Card subscription={SubscriptionType.PRO}>
            <Badge bgcolor={colors.white}>
              <Typography color={colors.black}>{t("subscription.badge.PRO")}</Typography>
            </Badge>
            <SubscriptionBadge disable subscription={SubscriptionType.PRO} />
            {getSubscriptionCost(SubscriptionType.PRO)}
            {getSubscriptionButton(SubscriptionType.PRO)}
            <Stack direction={"column"} spacing={1.5}>
              {features[SubscriptionType.PRO].map((feature) => getFeature(SubscriptionType.PRO, feature, "#D4A338"))}
            </Stack>
            {actualSubscription === SubscriptionType.PRO
              && !creditsRechargeType
              && getAutomaticMonthlyPaymentButton()}
          </Card>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="center" sx={{ mt: 2.75 }}>
        <Button variant="outlined" component={Link} to="/predplatne" fullWidth={isMobile}>
          {t("subscription.detailComparison")}
        </Button>
      </Box>
      {modalDowngradingFailOpen && (
        <ModalDowngradingFail
          open={modalDowngradingFailOpen}
          onClose={onModalDowngradingFailClose}
          data={subscriptionResponse}
        />
      )}
      {modalNotEnoughCreditsOpen && (
        <ModalNotEnoughCredits open={modalNotEnoughCreditsOpen} onClose={onModalNotEnoughCredits} />
      )}
    </Panel>
  );
};

export default Subscription;
